import { RealtimeEvent } from './types';

type Listener = (evt: RealtimeEvent) => void;

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private listeners: Set<Listener> = new Set();
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private url: string;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private subscribedTopics: Set<string> = new Set();

  constructor(baseUrl: string) {
    this.url = baseUrl.replace(/\/$/, '');
  }

  connect(token: string): void {
    // If token changed, disconnect and reconnect with new token
    if (this.token !== token && this.socket) {
      this.disconnect();
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.token = token;
    this.attemptConnection();
  }

  private attemptConnection(): void {
    // Allow empty token for anonymous connections
    const wsUrl = this.buildWsUrl(this.token || '');
    this.socket = new WebSocket(wsUrl);

    this.socket.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        this.listeners.forEach(l => l(data));
      } catch (error) {
        console.warn('[RealtimeClient] Failed to parse message:', error);
      }
    };

    this.socket.onopen = () => {
      console.log('[RealtimeClient] Connected successfully');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.resubscribeToTopics();
    };

    this.socket.onclose = event => {
      console.log(
        '[RealtimeClient] Connection closed:',
        event.code,
        event.reason,
      );
      this.stopHeartbeat();
      this.scheduleReconnect();
    };

    this.socket.onerror = error => {
      console.error('[RealtimeClient] WebSocket error:', error);
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[RealtimeClient] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts - 1),
      30000,
    );

    console.log(
      `[RealtimeClient] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.attemptConnection();
    }, delay);
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.stopReconnectTimer();
    this.socket?.close();
    this.socket = null;
    this.token = null;
  }

  private stopReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  subscribe(topics: string[]): void {
    topics.forEach(topic => this.subscribedTopics.add(topic));
    this.send({ type: 'subscribe', topics });
  }

  unsubscribe(topics: string[]): void {
    topics.forEach(topic => this.subscribedTopics.delete(topic));
    this.send({ type: 'unsubscribe', topics });
  }

  private resubscribeToTopics(): void {
    if (this.subscribedTopics.size > 0) {
      const topics = Array.from(this.subscribedTopics);
      console.log('[RealtimeClient] Resubscribing to topics:', topics);
      this.send({ type: 'subscribe', topics });
    }
  }

  onEvent(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get isConnected(): boolean {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  private send(payload: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'pong' });
    }, 25000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private buildWsUrl(token: string): string {
    const base = this.url;
    const isSecure = base.startsWith('https://');
    const wsProtocol = isSecure ? 'wss' : 'ws';
    const hostPortAndPath = base.replace(/^https?:\/\//, '');
    const slashIdx = hostPortAndPath.indexOf('/');
    const hostPort =
      slashIdx >= 0 ? hostPortAndPath.slice(0, slashIdx) : hostPortAndPath;

    // If no token, connect without token param - backend accepts anonymous connections
    if (!token) {
      return `${wsProtocol}://${hostPort}/api/v1/ws`;
    }

    return `${wsProtocol}://${hostPort}/api/v1/ws?token=${encodeURIComponent(
      token,
    )}`;
  }
}
